import { memo } from 'react';

import MiniMwdSvg from '@/apps/mwd-v2/components/MiniMwd/MiniMwdSvg';
import SelectedWerklijn from '@/apps/mwd-v2/components/MiniMwd/SelectedWerklijn';
import StickyDayLabel from '@/apps/mwd-v2/components/MiniMwd/StickyDayLabel';
import TickDays from '@/apps/mwd-v2/components/Timescale/TickDays';
import TickHours from '@/apps/mwd-v2/components/Timescale/TickHours';
import TickSvg from '@/apps/mwd-v2/components/Timescale/TickSvg';
import IngeregeldXs from '@/assets/icons/IngeregeldXs';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';

import { useMiniMwd } from './useMiniMwd';

import styles from './MiniMwd.module.css';

export type { MiniMwdLegendItem, MiniMwdProps } from './miniMwdTypes';
import type { MiniMwdProps } from './miniMwdTypes';

const WerklijnenDiagram = memo(SelectedWerklijn);

const MiniMwd = ({
  dialogData,
  hideFetchToast,
  eenheidNummers,
  legendItems,
  selectedLegendIds,
  onToggleLegendItem,
}: MiniMwdProps) => {
  const {
    mwdChartRef,
    miniScrollLeft,
    handleScroll,
    hasLegend,
    legendRows,
    werklijnIdsToRender,
    svgHeight,
    dayAfterTomorrow,
    miniMwdWidth,
    miniMwdScale,
  } = useMiniMwd({ dialogData, hideFetchToast, eenheidNummers, legendItems, selectedLegendIds });

  return (
    <div className={styles.wrapper}>
      <div className={hasLegend ? styles.bodyWithLegend : undefined}>
        {hasLegend && (
          <div className={styles.legendColumn} data-testid='mini-mwd-legend'>
            <div className={styles.legendTopSpacer} />
            {legendRows.map((row) => {
              const isExternIngeregeld = row.isExternIngeregeld ?? row.isIngeregeld ?? false;
              return (
                <label key={row.id} className={styles.legendItem} data-selected={row.checked || undefined}>
                  <Checkbox
                    name={`mini-mwd-legend-${row.id}`}
                    value={row.checked}
                    disabled={row.disabled}
                    onChange={(checked) => onToggleLegendItem?.(row.id, checked)}
                  />
                  <span className={styles.legendText}>
                    <span className={styles.legendNummerRow}>
                      <span className={styles.legendNummer}>{row.nummer}</span>
                      {row.isIngeregeld && (
                        <IngeregeldXs
                          width={14}
                          height={14}
                          className={styles.legendIngeregeldIcon}
                          fill={
                            isExternIngeregeld
                              ? 'var(--tractie-color-content-destructive-default)'
                              : 'var(--tractie-color-content-interactive-default)'
                          }
                          aria-label={
                            isExternIngeregeld
                              ? 'Eenheid is al ingeregeld (niet via dit agenda-item)'
                              : 'Eenheid is ingeregeld via dit agenda-item'
                          }
                        />
                      )}
                    </span>
                    {row.subtype && <span className={styles.legendSubtype}>{row.subtype}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        )}
        <div className={styles.chartArea}>
          <StickyDayLabel scrollLeft={miniScrollLeft} />
          <div ref={mwdChartRef} className={styles.miniMwdScrollContainerStyle} onScrollCapture={handleScroll}>
            <div
              data-testid='miniMwdSvgContainer'
              id='miniMwdSvgContainer'
              className={styles.svgContainer}
              style={{
                height: svgHeight,
                background: `linear-gradient(90deg, var(--tractie-color-background-default) ${dayAfterTomorrow}px, var(--tractie-color-background-accent-gray-subtle) ${dayAfterTomorrow}px)`,
              }}
            >
              <TickSvg mini mwdWidth={miniMwdWidth}>
                <TickDays mini top={20} />
                <TickHours mini top={30} />
              </TickSvg>
              <MiniMwdSvg
                data-timescale-start={miniMwdScale.domain()[0]}
                data-timescale-end={miniMwdScale.domain()[1]}
                data-timescale-range={miniMwdScale.range()}
                miniMwdHeight={svgHeight}
                miniMwdWidth={miniMwdWidth}
              >
                <WerklijnenDiagram dialogData={dialogData} werklijnIds={werklijnIdsToRender} />
              </MiniMwdSvg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniMwd;
